import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import MailIcon from "@mui/icons-material/MailOutlineRounded";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useState } from "react";
import BadgeDetached from "@entrilia/shared/components/BadgeDetached";
import ShowMoreLessButton from "@entrilia/shared/components/ShowMoreLessButton";
import useToggleState from "@entrilia/shared/hooks/useToggleState";
import { getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import type { InboxItem } from "../../../../../api/types/inboxesTypes";
import type {
  UploadWorkflowAttachmentResult,
  Workflow,
  WorkflowFileInfo,
} from "../../../../../api/types/workflowTypes";
import DeleteWorkflowAttachmentDialog from "./DeleteWorkflowAttachmentDialog";
import UploadWorkflowAttachmentDialog from "./UploadWorkflowAttachmentDialog";
import WorkflowDocumentsSectionAttachmentRow from "./WorkflowDocumentsSectionAttachmentRow";

interface Props {
  workflowId: string;
  triggerInboxItem?: InboxItem;
  documentFiles?: WorkflowFileInfo[];
  onOpenAttachmentPreview: (attachment: WorkflowFileInfo) => void;
  onOpenEmailPreview: () => void;
  onFileUploaded: (result: UploadWorkflowAttachmentResult) => void;
  onFileDeleted: (updatedWorkflow: Workflow, fileCatalogId: string, fileName: string) => void;
  canManageFiles: boolean;
}

const excludedAttachmentExtensions = ["", ".eml", ".md"];
const maxAttachmentsToShow = 2;

const WorkflowDocumentsSection = ({
  workflowId,
  triggerInboxItem,
  documentFiles,
  onOpenAttachmentPreview,
  onOpenEmailPreview,
  onFileUploaded,
  onFileDeleted,
  canManageFiles,
}: Props) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorkflowFileInfo>();
  const [showAllAttachments, toggleShowAllAttachments] = useToggleState(false);

  const attachmentFiles = (documentFiles ?? []).filter(
    (doc) => !excludedAttachmentExtensions.includes(getFileExtension(doc.fileName))
  );

  const canToggleAttachments = attachmentFiles.length - maxAttachmentsToShow > 1;

  const visibleAttachments =
    canToggleAttachments && !showAllAttachments ? attachmentFiles.slice(0, maxAttachmentsToShow) : attachmentFiles;

  const hasEmail = triggerInboxItem?.channelType === "Email";

  return (
    <>
      <Stack spacing={2} sx={{ p: 2 }}>
        {hasEmail && (
          <Stack spacing={1}>
            <Typography variant="subtitle2">Email</Typography>
            <Paper
              variant="outlined"
              onClick={onOpenEmailPreview}
              sx={(t) => ({ cursor: "pointer", "&:hover": { borderColor: t.palette.primary.main } })}
            >
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", px: 1.5, py: 1 }}>
                <MailIcon color="action" />
                <Typography noWrap>{triggerInboxItem.subject || "Email"}</Typography>
              </Stack>
            </Paper>
          </Stack>
        )}

        <Stack spacing={1}>
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="subtitle2">Attachments</Typography>
              <BadgeDetached badgeContent={attachmentFiles.length} color="secondary" />
            </Stack>
            <Button
              variant="text"
              color="secondary"
              startIcon={<FileUploadOutlined />}
              disabled={!canManageFiles}
              onClick={() => setUploadDialogOpen(true)}
            >
              Upload
            </Button>
          </Stack>
          <Stack spacing={1} sx={{ maxHeight: "20rem", overflow: "auto" }}>
            {visibleAttachments.map((attachment) => (
              <WorkflowDocumentsSectionAttachmentRow
                key={attachment.fileCatalogId}
                workflowId={workflowId}
                attachment={attachment}
                canDelete={canManageFiles}
                onOpenPreview={onOpenAttachmentPreview}
                onDeleteRequested={setDeleteTarget}
              />
            ))}
          </Stack>
          {canToggleAttachments && (
            <Box component="span" sx={{ alignSelf: "start" }}>
              <ShowMoreLessButton
                buttonColor="secondary"
                expanded={showAllAttachments}
                onClick={toggleShowAllAttachments}
              />
            </Box>
          )}
        </Stack>
      </Stack>
      {uploadDialogOpen && (
        <UploadWorkflowAttachmentDialog
          workflowId={workflowId}
          onboard
          existingFileNames={(documentFiles ?? []).map((documentFile) => documentFile.fileName)}
          onClose={() => setUploadDialogOpen(false)}
          onUploaded={onFileUploaded}
        />
      )}
      {deleteTarget && (
        <DeleteWorkflowAttachmentDialog
          workflowId={workflowId}
          attachment={deleteTarget}
          onClose={() => setDeleteTarget(undefined)}
          onDeleted={(updatedWorkflow) => {
            const { fileCatalogId, fileName } = deleteTarget;
            setDeleteTarget(undefined);
            onFileDeleted(updatedWorkflow, fileCatalogId, fileName);
          }}
        />
      )}
    </>
  );
};

export default WorkflowDocumentsSection;
